<template>
    <div class="flex flex-wrap min-h-32" id="modalSelectedIndicators" v-if="form">
        <div class="w-full">
            <label for="apply">Apply to:
                <select name="apply" class="form-control form-control-sm" v-model="form.placement">
                    <option v-for="apply in applyTo" :value="apply.value">{{ apply.label }}</option>
                </select>
            </label>
        </div>

        <div class="xs:w-full w-1/2 mt-3">
            <div class="indicator-curves" v-for="(curve, index) in form.curves">
                <div class="curve-settings" v-for="(color, colorIndex) in curve.colors">
                    <div v-if="displayColor(curve.fields[0], colorIndex)" class="h-14 min-h-14">
                        <div class="inline-flex">
                            <i v-if="curve.visible" @click="curve.visible = false" class="self-center far fa-eye"></i>
                            <i v-if="!curve.visible" @click="curve.visible = true" class="self-center far fa-eye-slash text-gray-400"></i>

                            <div class="font-bold ml-2" v-if="isVolume(curve.colors)">
                                {{ getVolumeLabel(colorIndex) }}
                            </div>
                            <div class="font-bold ml-2" v-else>
                                {{ getField(curve.fields[0], curve.style, colorIndex, (form.id || form.studyId)) }}
                            </div>
                        </div>
                        <div class="flex flex-wrap">
                            <div class="inline-flex">
                                <color-picker :initial-color="form.curves[index].colors[colorIndex]" v-model="form.curves[index].colors[colorIndex]"></color-picker>
                                <bar-thickness v-if="isNotRibbon(curve.style) && colorIndex === 0" v-model="curve.lineWidth" :initial-value="curve.lineWidth"></bar-thickness>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <div class="xs:w-full w-1/2 mt-3">
            <div class="h-14 min-h-14" v-for="(input, index) in form.inputs">
                <label :for="input.name">
                    {{ input.name }}
                    <select class="form-control form-control-sm" v-model="form.inputs[index].value" v-if="hasEnumerations(input.name)" :name="input.name" :id="input.name">
                        <option v-for="enumeration in findEnumerationValues(input.name)" :value="enumeration">{{ enumeration }}</option>
                    </select>
                    <input v-else class="form-input-inline form-input-sm-inline" type="number" :name="input.name" v-model.number="form.inputs[index].value">
                </label>
            </div>

            <template v-for="(level, index) in form.levels">
                <div class="h-14 min-h-14" v-if="level.name">
                    <label for="">{{ level.name }}</label>
                    <input class="form-input-inline form-input-sm-inline" type="number" v-model.number="form.levels[index].value">
                </div>
            </template>
        </div>

    </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeMount, nextTick } from 'vue';
import ColorPicker from './ColorPicker';
import BarThickness from './BarThickness';

const props = defineProps({
    indicator: {
        type: Object,
        required: true
    },
    defaultPlacement: {
        type: String,
        required: true
    },
    modelValue: [String, Number, Object],
});

const $clone = (obj) => JSON.parse(JSON.stringify(obj));
const emit = defineEmits(['update:modelValue']);

const form = ref($clone(props.indicator));
const fields = ref([]);
const applyTo = ref([
    { label: 'Main Window', value: (props.defaultPlacement === 'standalone' || props.defaultPlacement === 'withMain') ? 'withMain' : 'overlay' },
    { label: 'New Pane', value: 'standalone' }
]);

function getField(fieldName, style, index, studyId) {
    if (style === 'Ribbon') return 'Fill';
    if (fieldName === 'OpenInterest') return 'Open Interest';
    if (fieldName === 'TRSP1') return 'Trend Seeker';
    if (fieldName === 'TRSP2') return 'Trend Seeker';
    if (studyId === 'CTMF' && fieldName === 'CTMF' && index === 0) return 'Down Momentum';
    if (studyId === 'CTMF' && fieldName === 'CTMF' && index === 1) return 'Up Momentum';
    if (studyId === 'TPI' && fieldName === 'PercK') return 'Turning Point';

    if ((studyId === 'RSI' || studyId === 'RSIMOD' || studyId === 'RSISTO') && index === 0) return 'Oversold';
    if ((studyId === 'RSI' || studyId === 'RSIMOD' || studyId === 'RSISTO') && index === 3) return 'Overbought';

    if (studyId === 'FI' && index === 0) return 'Below';
    if (studyId === 'FI' && index === 1) return 'Above';

    const field = fields.value.find(f => f.id === fieldName);

    if (field) {
        return field.name;
    }
}

function displayColor(fieldName, colorIndex) {
    if (fieldName && (fieldName.startsWith('RSI') || fieldName === 'STORSI') && colorIndex === 1) return false;
    return true;
}

const isVolume = (colors) => {
    return form.value.id === 'Volume';
    // return colors.length === 3;
};

const isNotRibbon = (style) => {
    return style !== 'Ribbon';
};

const getVolumeLabel = (index) => {
    if (index === 0) {
        return 'Down'
    } else if (index === 1) {
        return 'Up';
    } else {
        return 'Unch';
    }
};

const hasEnumerations = (name) => {
    if (props.indicator.meta && props.indicator.meta.enumerations && props.indicator.meta.enumerations.length) {
        return props.indicator.meta.enumerations.find(e => e.input === name);
    }
};

const findEnumerationValues = (name) => {
    return props.indicator.meta.enumerations.find(e => e.input === name).values;
};

function getChartFields() {
    const fields = window.Barchart.RealtimeWidgets.Charts.getTaxonomies().studyFields;

    return fields.map((field) => {
        if (field.id === 'ENVEXPU' || field.id === 'ENVSMOU') {
            field.name = 'Up';
        }

        if (field.id === 'ENVEXPD' || field.id === 'ENVSMOD') {
            field.name = 'Down';
        }

        if (field.id === 'CCI') {
            field.name = 'CCI';
        }

        if (field.id === 'CTM' || field.id === 'CTMF') {
            field.name = 'Cycle Trading';
        }

        if (field.id === 'CTLTM' || field.id === 'CTLTMF') {
            field.name = 'Cycle Long';
        }

        return field;
    });
}

watch(form, (newValue, oldValue) => {
    emit('update:modelValue', newValue);
}, { deep: true });

onBeforeMount(() => {
});

onMounted(() => {
    nextTick(() => {
        fields.value = getChartFields();
        emit('update:modelValue', form.value);
    });
});
</script>
